import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { configuracao } from '../configuracao.js';

/**
 * Sessão do operador no Cognito.
 *
 * A biblioteca guarda os tokens no localStorage e renova o access token sozinha a partir
 * do refresh token. Só encapsulamos aqui o que o cockpit precisa: entrar, sair, pegar o
 * token para o header Authorization e trocar a senha temporária no primeiro acesso.
 */
const pool = new CognitoUserPool({
  UserPoolId: configuracao.cognito.poolId,
  ClientId: configuracao.cognito.clienteId,
});

export interface OperadorLogado {
  nome: string;
  email: string;
  papeis: string[];
}

/** Resultado do login: entrou, ou precisa definir a senha definitiva. */
export type ResultadoDoLogin =
  | { tipo: 'entrou'; operador: OperadorLogado }
  | { tipo: 'nova-senha'; concluir: (novaSenha: string) => Promise<OperadorLogado> };

function operadorDaSessao(sessao: CognitoUserSession): OperadorLogado {
  const claims = sessao.getIdToken().payload as Record<string, unknown>;
  const grupos = claims['cognito:groups'];

  return {
    nome: String(claims.name ?? claims.email ?? ''),
    email: String(claims.email ?? ''),
    papeis: Array.isArray(grupos) ? grupos.map(String) : [],
  };
}

export function entrar(email: string, senha: string): Promise<ResultadoDoLogin> {
  const usuario = new CognitoUser({ Username: email, Pool: pool });
  const credenciais = new AuthenticationDetails({ Username: email, Password: senha });

  return new Promise((resolver, rejeitar) => {
    usuario.authenticateUser(credenciais, {
      onSuccess: (sessao) => resolver({ tipo: 'entrou', operador: operadorDaSessao(sessao) }),
      onFailure: (erro: Error) => rejeitar(traduzirErro(erro)),
      newPasswordRequired: () => {
        resolver({
          tipo: 'nova-senha',
          concluir: (novaSenha) =>
            new Promise((ok, falha) => {
              // O Cognito recusa reenviar atributos imutáveis na troca de senha.
              usuario.completeNewPasswordChallenge(
                novaSenha,
                {},
                {
                  onSuccess: (sessao) => ok(operadorDaSessao(sessao)),
                  onFailure: (erro: Error) => falha(traduzirErro(erro)),
                },
              );
            }),
        });
      },
    });
  });
}

export function sair(): void {
  pool.getCurrentUser()?.signOut();
}

/** Sessão válida do operador, ou `null` se não há ninguém logado. */
export function sessaoAtual(): Promise<{ operador: OperadorLogado; token: string } | null> {
  const usuario = pool.getCurrentUser();
  if (!usuario) return Promise.resolve(null);

  return new Promise((resolver) => {
    usuario.getSession((erro: Error | null, sessao: CognitoUserSession | null) => {
      if (erro || !sessao?.isValid()) {
        resolver(null);
        return;
      }
      resolver({
        operador: operadorDaSessao(sessao),
        token: sessao.getIdToken().getJwtToken(),
      });
    });
  });
}

/** Token para o header Authorization. Renova sozinho quando está perto de expirar. */
export async function tokenAtual(): Promise<string | null> {
  return (await sessaoAtual())?.token ?? null;
}

const MENSAGENS: Record<string, string> = {
  NotAuthorizedException: 'E-mail ou senha incorretos.',
  UserNotFoundException: 'E-mail ou senha incorretos.',
  PasswordResetRequiredException: 'Sua senha precisa ser redefinida. Procure a operação.',
  UserNotConfirmedException: 'Cadastro ainda não confirmado. Procure a operação.',
  TooManyRequestsException: 'Muitas tentativas seguidas. Aguarde um minuto e tente de novo.',
  LimitExceededException: 'Muitas tentativas seguidas. Aguarde um minuto e tente de novo.',
  InvalidPasswordException:
    'A senha precisa ter ao menos 12 caracteres, com maiúscula, minúscula, número e símbolo.',
};

function traduzirErro(erro: Error): Error {
  const nome = (erro as Error & { code?: string }).code ?? erro.name;
  return new Error(MENSAGENS[nome] ?? erro.message ?? 'Não foi possível entrar.');
}
